import React, { FC } from 'react';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import aligned from '@ui/styled/Aligned';
import spaced from '@ui/styled/Spaced';

import useWindowSize from '@hooks/useWindowSize';

import Checkmark from '@assets/vectors/checkmark-task.inline.svg';

import theme from '@theme/FolxTheme';

const AlignedRow = aligned(FlexRow);

const DesktopSpacedColumn = spaced(styled(FlexColumn)`
	width: 40rem;
`);

const SpacedColumn = spaced(FlexColumn);

interface IDashboardTask {
	heading: string;
	description: string;
	image: string;
	alt: string;
	done?: boolean;
	onClick?: () => void;
	loading?: boolean;
}

const CheckmarkContainer = styled.div`
	position: absolute;
	right: 4.4rem;
	top: 2.2rem;
	svg {
		width: 4rem;
		height: 4rem;
	}
`;

const DashboardTaskCard = styled(BaseCard)<{
	done: boolean;
	onClick?: () => void;
	isMobile: boolean;
}>`
	all: unset;
	background-color: white;
	position: relative;
	border-radius: ${theme.radius.xl};
	box-sizing: border-box;
	box-shadow: ${theme.shadow.sliderBox};
	min-height: 22rem;
	width: ${({ isMobile }) => (isMobile ? '32rem' : '%100')};
	padding: 1.4rem;
	${({ done }) => !done && `cursor: pointer;`}

	:hover {
		background-color: ${({ done }) => (done ? theme.colors.white : theme.colors.lightGray)};
	}

	${BasicText} {
		${({ done }) => done && `color: ${theme.colors.placeholderGray};`}
	}

	img {
		${({ done }) => done && `opacity: 0.4;`}
	}

	@media ${theme.responsive.breakpoints.tablet} {
		min-height: unset;
	}
`;

const Photo = styled.div`
	height: 10rem;
	width: 10rem;
	border-radius: 50%;
	background-color: ${theme.colors.lightGray};
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;

	img {
		height: 100%;
		width: 100%;
		object-fit: cover;
	}
`;

const DashboardTask: FC<IDashboardTask> = ({
	heading,
	description,
	image,
	alt,
	done = false,
	onClick,
	loading = false,
}) => {
	const { isMobile } = useWindowSize();

	const Mobile = () => (
		<SpacedColumn gap="xssm" marginBottom="xs" marginTop="xs">
			<Photo>
				<img src={image} alt={alt} />
			</Photo>
			<BasicText variant="field">{heading}</BasicText>
			<BasicText variant="fineprint">{description}</BasicText>
		</SpacedColumn>
	);

	const Desktop = () => (
		<AlignedRow gap="lg" alignItems="center">
			<Photo>
				<img src={image} alt={alt} />
			</Photo>
			<DesktopSpacedColumn gap="xssm">
				<BasicText variant="field">{heading}</BasicText>
				<BasicText variant="fineprint">{description}</BasicText>
			</DesktopSpacedColumn>
		</AlignedRow>
	);

	const MobileSkeleton = () => (
		<SpacedColumn gap="xssm" marginBottom="xs" marginTop="xs">
			<Photo>
				<Skeleton borderRadius="half" structure="single" fullHeight fullWidth />
			</Photo>
			<Skeleton structure="single" height="md" fullWidth />
			<Skeleton structure="single" height="sm" fullWidth />
		</SpacedColumn>
	);

	const DesktopSkeleton = () => (
		<AlignedRow gap="lg" alignItems="center">
			<Photo>
				<Skeleton borderRadius="half" structure="single" fullHeight fullWidth />
			</Photo>
			<DesktopSpacedColumn gap="xssm">
				<Skeleton structure="single" height="md" fullWidth />
				<Skeleton structure="single" height="sm" fullWidth />
			</DesktopSpacedColumn>
		</AlignedRow>
	);

	return (
		<DashboardTaskCard
			as="button"
			aria-label={loading ? 'Task Loading' : done ? 'Task Complete' : 'Task Incomplete'}
			isMobile={isMobile}
			onClick={onClick}
			done={done}
			radius="md"
			bgColor="white"
			borderless>
			<LoadingWrapper
				loading={loading}
				skeleton={isMobile ? <MobileSkeleton /> : <DesktopSkeleton />}>
				{done && (
					<CheckmarkContainer>
						<Checkmark />
					</CheckmarkContainer>
				)}
				{isMobile ? <Mobile /> : <Desktop />}
			</LoadingWrapper>
		</DashboardTaskCard>
	);
};

export default DashboardTask;
